import { useQuery } from "@apollo/client";
import {
  PageFiltersContextProvider,
  QueryToolbar,
  usePageFilters,
  useTablePagination,
  useTableSorting,
} from "@aurem/components";
import {
  EmptyStateProps,
  Table,
  TableColumn,
  TablePagination,
} from "@aurem/ui";
import { useBreadcrumbs } from "components/Topbar/PageTitle";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useMemo } from "react";
import { SortDirection } from "types";
import { PageLayout } from "../../../layouts/PageLayout";
import { renderField } from "../field-renderers";
import { buildGraphQLFilters, getNestedValue } from "../filter-utils";
import { InternalResourceConfig } from "../types";

type ResourceListPageProps = {
  config: InternalResourceConfig;
};

const ResourceListView = ({ config }: ResourceListPageProps) => {
  const { t } = useI18n();
  const displayName = t(config.displayNameKey);
  const { filters, setFilters, removeFilters } =
    usePageFilters<Record<string, string>>();
  const detailsParamKey = config.details?.paramKey ?? "id";

  const page = filters?.page ? Number(filters.page) : 1;
  const pageSize = filters?.pageSize ? Number(filters.pageSize) : 25;

  const graphqlFilters = useMemo(
    () => buildGraphQLFilters(config, filters),
    [config, filters],
  );

  const orderBy = useMemo(() => {
    if (filters.sortKey) {
      return {
        column: filters.sortKey,
        direction:
          filters.sortOrder === "asc" ? SortDirection.Asc : SortDirection.Desc,
      };
    }

    return {
      column: config.list.defaultSort.column,
      direction:
        config.list.defaultSort.direction === "asc"
          ? SortDirection.Asc
          : SortDirection.Desc,
    };
  }, [filters.sortKey, filters.sortOrder, config.list.defaultSort]);

  const search = config.list.searchArg
    ? filters.search?.trim() || undefined
    : undefined;

  const { data, loading } = useQuery(config.listQuery, {
    variables: {
      pagination: {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      filters: graphqlFilters,
      orderBy,
      search,
    },
  });

  const { items, totalItems } = useMemo(() => {
    const queryData = data?.[config.listQueryName];

    if (Array.isArray(queryData)) {
      return { items: queryData, totalItems: queryData.length };
    }

    return {
      items: queryData?.list ?? [],
      totalItems: queryData?.count ?? 0,
    };
  }, [data, config.listQueryName]);

  const columns: TableColumn[] = useMemo(
    () =>
      config.fields.map((field) => ({
        name: t(field.labelKey),
        key: field.key,
        width: field.width,
        sortable: !!field.sortKey,
        sortKey: field.sortKey,
      })),
    [config.fields, t],
  );

  const onRenderCell = useCallback(
    (column: TableColumn, item: unknown) => {
      const fieldConfig = config.fields.find((f) => f.key === column.key);

      if (!fieldConfig) {
        return String(getNestedValue(item, column.key) ?? "-");
      }

      const value = getNestedValue(item, fieldConfig.key);

      if (fieldConfig.format) {
        return fieldConfig.format(value, item);
      }

      return renderField(fieldConfig.type, value, item, fieldConfig.enumValues);
    },
    [config.fields],
  );

  const emptyState: EmptyStateProps | undefined = useMemo(() => {
    if (loading || items.length > 0) {
      return undefined;
    }

    return {
      title: t("apps.admin.common.data.notFound", displayName),
      text: filters.search
        ? t("apps.admin.common.data.noResultsFor", filters.search)
        : t("apps.admin.common.data.noRecords"),
    };
  }, [loading, items, displayName, filters.search, t]);

  const pagination: TablePagination = useTablePagination<
    Record<string, string>
  >({
    filters,
    setFilters,
    totalItems,
    pageKey: "page",
    pageSizeKey: "pageSize",
  });

  const sorting = useTableSorting<Record<string, string>>({
    filters,
    setFilters,
    removeFilters,
    sortFieldKey: "sortKey",
    sortOrderKey: "sortOrder",
  });

  const handleChangeSearch = useCallback(
    (search: string) => {
      setFilters({ search, page: "1" });
    },
    [setFilters],
  );

  const handleClearSearch = useCallback(() => {
    setFilters({ search: "", page: "1" });
  }, [setFilters]);

  return (
    <div className="flex flex-col gap-6 flex-grow min-h-0 w-full">
      <QueryToolbar
        inputProps={{
          placeholder: t(
            "apps.admin.common.data.searchPlaceholder",
            displayName.toLowerCase(),
          ),
          value: filters?.search || "",
          onChange: handleChangeSearch,
          onClear: handleClearSearch,
        }}
      />

      <Table
        columns={columns}
        items={items}
        emptyState={emptyState}
        pagination={pagination}
        sorting={sorting}
        loading={loading}
        onRenderCell={onRenderCell}
        onRowClick={
          config.details
            ? (item) => {
                const id = String((item as { id?: string }).id ?? "");
                if (!id) return;
                setFilters({ [detailsParamKey]: id });
              }
            : undefined
        }
      />

      {config.details && (
        <config.details.component
          selectedId={filters[detailsParamKey] ?? ""}
          onClose={() => setFilters({ [detailsParamKey]: "" })}
        />
      )}
    </div>
  );
};

export const ResourceListPage = ({ config }: ResourceListPageProps) => {
  const { t } = useI18n();
  const displayName = t(config.displayNameKey);
  const lastSegment = config.basePath.split("/").filter(Boolean).pop() ?? "";
  useBreadcrumbs(displayName ? [{ label: displayName }] : []);
  const detailsParamKey = config.details?.paramKey ?? "id";

  const defaultFilters = useMemo(
    () => ({
      search: "",
      page: "1",
      pageSize: "25",
      ...(config.details ? { [detailsParamKey]: "" } : {}),
    }),
    [config.details, detailsParamKey],
  );

  return (
    <PageLayout className="min-h-0">
      <PageFiltersContextProvider defaultFilters={defaultFilters}>
        <ResourceListView config={config} />
      </PageFiltersContextProvider>
    </PageLayout>
  );
};
